import React from 'react';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import ReferencesAndAuthor from '../components/ReferencesAndAuthor';
import Footer from '../components/Footer';
import { LAST_REVIEWED, formatarData } from '../data/revision';

const Section = ({ title, rows }) => (
  <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6 overflow-x-auto">
    <h2 className="text-2xl font-bold mb-4">{title}</h2>
    <table className="w-full border-collapse min-w-[620px]">
      <thead><tr className="bg-slate-50"><th className="border p-3 text-left">Pontos</th><th className="border p-3 text-left">Resposta</th><th className="border p-3 text-left">Descrição</th></tr></thead>
      <tbody>{rows.map(([score, response, description]) => <tr key={`${score}-${response}`}><td className="border p-3 font-bold">{score}</td><td className="border p-3">{response}</td><td className="border p-3 text-gray-600">{description}</td></tr>)}</tbody>
    </table>
  </section>
);

function Tabela() {
  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 print:bg-white">
      <Header />
      <Breadcrumb />
      <main className="max-w-5xl mx-auto px-4 py-12 print:py-0">
        <span className="section-kicker">Referência rápida</span>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mt-2 mb-4 text-slate-800">Tabela da Escala de Coma de Glasgow</h1>
        <p className="text-lg text-gray-600 mb-8">Critérios adultos de abertura ocular, resposta verbal e resposta motora, com regra explícita para componentes Não Testáveis (NT).</p>

        <Section title="Abertura ocular (O): como pontuar de 1 a 4" rows={[
          ['4', 'Espontânea', 'Abre os olhos sem estímulo'],
          ['3', 'Ao som', 'Abre após solicitação verbal'],
          ['2', 'À pressão', 'Abre após estímulo de pressão'],
          ['1', 'Ausente', 'Não abre os olhos'],
          ['NT', 'Não Testável', 'Edema periorbital ou trauma ocular'],
        ]} />

        <Section title="Resposta verbal (V): como pontuar de 1 a 5" rows={[
          ['5', 'Orientada', 'Responde de forma orientada'],
          ['4', 'Confusa', 'Conversa, porém desorientada'],
          ['3', 'Palavras', 'Palavras isoladas inteligíveis'],
          ['2', 'Sons', 'Gemidos ou sons incompreensíveis'],
          ['1', 'Ausente', 'Sem resposta verbal'],
          ['NT', 'Não Testável', 'Intubação, traqueostomia ou afasia que impeça avaliação confiável'],
        ]} />

        <Section title="Resposta motora (M): como pontuar de 1 a 6" rows={[
          ['6', 'Obedece comandos', 'Executa ação simples solicitada'],
          ['5', 'Localiza a pressão', 'Localiza o estímulo e tenta removê-lo'],
          ['4', 'Flexão normal', 'Retirada rápida ao estímulo'],
          ['3', 'Flexão anormal', 'Padrão de decorticação'],
          ['2', 'Extensão', 'Padrão de descerebração'],
          ['1', 'Ausente', 'Sem resposta motora'],
          ['NT', 'Não Testável', 'Bloqueio neuromuscular, sedação profunda ou limitação motora'],
        ]} />

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h2 className="text-2xl font-bold mb-3">Como somar e registrar o escore da Escala de Glasgow?</h2>
          <p className="text-gray-700 mb-3">Quando O, V e M são testáveis, some os três componentes: <strong>ECG = O + V + M</strong>. Registre também a notação completa, por exemplo <strong>O4 V5 M6 = ECG 15</strong>, porque pacientes com o mesmo total podem ter padrões neurológicos diferentes.</p>
          <a className="text-teal-700 font-semibold" href="/#calculadora">→ Abrir calculadora</a>
        </section>

        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-2xl font-bold mb-3">Quando não somar o escore total?</h2>
          <p className="text-gray-800">Se qualquer componente estiver <strong>Não Testável (NT)</strong>, não force pontuação 1 e não produza total. Registre os componentes disponíveis e o fator de interferência, como <strong>O4 V(NT) M6 — intubado</strong>. Veja exemplos em <a className="text-teal-700 font-semibold" href="/como-avaliar">Como avaliar</a>.</p>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h2 className="text-2xl font-bold mb-3">Tabela pediátrica</h2>
          <p className="text-gray-700">A escala pediátrica usa descritores diferentes para crianças pré-verbais, mantendo O 1–4, V 1–5 e M 1–6. Veja a <a className="text-teal-700 font-semibold" href="/pediatrica">tabela pediátrica completa e a calculadora com resultado</a>.</p>
        </section>

        <section className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 mb-8 print:hidden">
          <h2 className="text-2xl font-bold text-white mb-3">Versão para impressão</h2>
          <p className="text-slate-100 mb-4">Imprima esta página ou use o PDF A4 preparado com as tabelas adulta e pediátrica.</p>
          <a className="button button--primary" href="/escala-de-glasgow.pdf">Baixar PDF A4</a>
        </section>

        <ReferencesAndAuthor />
        <div className="print-footer" aria-hidden="true">
          escaladeglasgow.com.br · Dr. Carlos Felipe CRM/MG 105.721 · rev. {formatarData(LAST_REVIEWED)}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Tabela;
